import React, { useMemo, useEffect, useState } from "react";
import { Whisper, Tooltip } from "rsuite";
import { connect } from "react-redux";

import { getCDRList, getFailedMDRList } from "actions/cdr";
import { renderMoney, servicePick, resolvePerPage } from "utils";
import { usePrevious } from "hooks";
import {
    DESC_SORT
} from "const";
import {
    LS_CDR_COLUMNS,
    LS_CDR_FAILED_COLUMNS,
    LS_CDR_PER_PAGE,
    LS_CDR_SMS_COLUMNS,
    LS_FAILED_MDR_PER_PAGE
} from "const/localStorageKeys";
import Table from "components/Table/TableServerSort";

const DEFAULT_SORT = { column: "datetime", type: DESC_SORT };


const CDRTable = (props) => {
    const [sort, setSort] = useState(DEFAULT_SORT);
    const {
        cdrMdrLoading,
        cdrMdrList,
        cdrMdrMore,
        cdrMdrCount,

        mdrFailedLoading,
        mdrFailedList,
        mdrFailedMore,
        mdrFailedCount,

        mdrFailedPerPage,
        cdrPerPage,

        perPage,
        page,
        service,
        filters,
        isFailedMdr
    } = props;

    const perPageLSKey = useMemo(() => {
        return isFailedMdr ? LS_FAILED_MDR_PER_PAGE : LS_CDR_PER_PAGE;
    }, [isFailedMdr]);


    const resolvedPerPage = useMemo(() => resolvePerPage(
        perPageLSKey,
        perPage,
    ), [perPage, perPageLSKey]);

    const [currentPerPage, setCurrentPerPage] = useState(resolvedPerPage);

    useEffect(() => {
        setCurrentPerPage(resolvedPerPage);
    }, [resolvedPerPage]);

    useEffect(() => {
        setSort(DEFAULT_SORT);
    }, [service, isFailedMdr]);

    const prevService = usePrevious(service);
    const prevIsFailedMdr = usePrevious(isFailedMdr);

    const loading = isFailedMdr ? mdrFailedLoading : cdrMdrLoading;
    const dataList = isFailedMdr ? mdrFailedList : cdrMdrList;
    // const more = isFailedMdr ? mdrFailedMore : cdrMdrMore;
    const count = isFailedMdr ? mdrFailedCount : cdrMdrCount;

    const columnSelectorLSKey = useMemo(() => {
        if (service) {
            return LS_CDR_COLUMNS;
        }

        return isFailedMdr ? LS_CDR_FAILED_COLUMNS : LS_CDR_SMS_COLUMNS;
    }, [service, isFailedMdr]);
    const columnSelector = localStorage.getItem(columnSelectorLSKey) && JSON.parse(localStorage.getItem(columnSelectorLSKey));

    const columns = useMemo(() => {
        const cdrColumns = [
            {
                label: "Date Time",
                dataKey: "datetime",
                formatData: "datetime_local", //datetime_en
                minWidth: 180,
                title: true,
                sortable: true
            },
            {
                label: "Originator",
                dataKey: "originator_name",
                minWidth: 280,
                sortable: true
            },
            {
                label: "Tprefix",
                fullLabel: "Originator Tprefix",
                dataKey: "tprefix_originator",
                width: 90,
                hideDefault: true,
                sortable: true
            },
            {
                label: "Cost",
                fullLabel: "Originator Cost",
                dataKey: "cost_originator",
                value: ({ cost_originator }) => {
                    return renderMoney(cost_originator);
                },
                width: 75,
                sortable: true,
                align: "right"
            },
            {
                label: "Originator Rate",
                fullLabel: "Originator Rate",
                dataKey: "rate_originator",
                value: ({ rate_originator }) => {
                    return renderMoney(rate_originator);
                },
                width: 120,
                sortable: true,
                align: "right"
            },
            {
                label: "A-Number",
                dataKey: "a_number",
                width: 120,
                sortable: true
            },
            {
                label: "Origin",
                dataKey: "a_subdestination_name",
                minWidth: 200,
                title: true,
                sortable: true
            },
            {
                label: "B-Number",
                dataKey: "b_number",
                width: 120,
                sortable: true
            },
            {
                label: "Destination",
                dataKey: "b_subdestination_name",
                minWidth: 280,
                title: true,
                sortable: true
            },
            {
                label: "Range",
                dataKey: "range",
                minWidth: 300,
                title: true,
                sortable: true
            },
            {
                label: "Dialer",
                dataKey: "dialer_name",
                value: ({ dialer_name, dialer_manager }) => `${dialer_name || "no dialer"} / ${dialer_manager || "no manager"}`,
                minWidth: 250,
                sortable: true
            },
            {
                label: "Dialer Nickname",
                dataKey: "dialer_nickname",
                value: ({ dialer_nickname }) => dialer_nickname,
                minWidth: 160,
                sortable: true
            },
            {
                label: "Trusted",
                fullLabel: "Trusted",
                dataKey: "dialer_is_reliable",
                value: ({ dialer_is_reliable }) => {
                    return dialer_is_reliable ? "Yes" : "No";
                },
                width: 80,
                sortable: true,
                align: "right"
            },
            {
                label: "TPrefix",
                fullLabel: "Dialer TPrefix",
                dataKey: "tprefix_dialer",
                width: 90,
                hideDefault: true,
                sortable: true
            },
            {
                label: "Dialer Rate",
                dataKey: "rate_dialer",
                fullLabel: "Dialer Rate",
                value: ({ rate_dialer, cur_name_dialer }) => {
                    return renderMoney(rate_dialer, cur_name_dialer);
                },
                width: 100,
                sortable: true,
                align: "right"
            },
            {
                label: "Cost",
                fullLabel: "Dialer Cost",
                dataKey: "cost_dialer",
                value: ({ cost_dialer }) => renderMoney(cost_dialer),
                width: 75,
                sortable: true,
                align: "right"
            },
            {
                label: "Term. Point",
                dataKey: "term_point",
                minWidth: 200,
                hideDefault: true,
                sortable: true
            },
            {
                label: "Duration",
                dataKey: "duration",
                value: ({ duration }) => duration || "0",
                width: 100,
                sortable: true
            },
            {
                label: "SIP Cause",
                dataKey: "sip_cause",
                width: 100,
                sortable: true,
                align: "right"
            },
            {
                label: "Margin",
                fullLabel: "Margin",
                dataKey: "margin",
                value: ({ margin, cur_name_margin }) => {
                    return renderMoney(margin, cur_name_margin, 5);
                },
                width: 110,
                sortable: true,
                align: "right"
            }
        ];

        const mdrColumns = [
            {
                label: "Date Time",
                dataKey: "datetime",
                formatData: "datetime_local", //datetime_en
                flexGrow: 1,
                minWidth: 180,
                title: true,
                sortable: true
            },
            {
                label: "Originator",
                dataKey: "originator_name",
                minWidth: 200,
                sortable: true
            },
            {
                label: "Operator",
                dataKey: "operator",
                minWidth: 200,
                sortable: true
            },
            {
                label: "Country",
                dataKey: "country",
                minWidth: 160,
                sortable: true
            },
            {
                label: "Supplier",
                dataKey: "supplier_name",
                minWidth: 200,
                sortable: true
            },
            {
                label: "Supplier Rate",
                dataKey: "supplier_rate",
                fullLabel: "Supplier Rate",
                value: ({ supplier_rate, supplier_cur_name }) => {
                    return renderMoney(supplier_rate, supplier_cur_name);
                },
                minWidth: 120,
                sortable: true,
                align: "right"
            },
            {
                label: "Supplier trunk",
                dataKey: "supplier_trunk",
                minWidth: 160,
                // sortable: true
            },
            {
                label: "Sender ID",
                dataKey: "senderid",
                width: 160,
                sortable: true
            },
            {
                label: "Phone",
                dataKey: "phone",
                minWidth: 180,
                sortable: true
            },
            {
                label: "Origin",
                dataKey: "sender_sde_name",
                minWidth: 200,
                title: true,
                sortable: true
            },
            {
                label: "Destination",
                dataKey: "phone_sde_name",
                minWidth: 280,
                title: true,
                sortable: true
            },
            {
                label: "Range",
                dataKey: "range_name",
                minWidth: 330,
                title: true,
                sortable: true
            },
            {
                label: "Dialer",
                dataKey: "dialer_name",
                value: ({ dialer_name, dialer_manager }) => {
                    return `${dialer_name || "no dialer"} / ${dialer_manager || "no manager"}`
                },
                minWidth: 250,
                sortable: true
            },
            {
                label: "Dialer Nickname",
                dataKey: "dialer_nickname",
                value: ({ dialer_nickname }) => dialer_nickname,
                minWidth: 160,
                sortable: true
            },
            {
                label: "Dialer Rate",
                dataKey: "dialer_rate",
                fullLabel: "Dialer Rate",
                value: ({ dialer_rate, dialer_cur_name }) => {
                    return renderMoney(dialer_rate, dialer_cur_name);
                },
                width: 100,
                sortable: true,
                align: "right"
            },
            {
                label: "Trusted",
                fullLabel: "Trusted",
                dataKey: "dialer_is_reliable",
                value: ({ dialer_is_reliable }) => {
                    return dialer_is_reliable ? "Yes" : "No";
                },
                width: 80,
                sortable: true,
                align: "right"
            },
            {
                label: "Reason",
                dataKey: "reason",
                value: ({ reason }) => {
                    return <a title={reason}>
                        {reason}
                    </a>
                },
                minWidth: 300,
                sortable: true
            },
            {
                label: "MCC/MNC",
                dataKey: "mcc_mnc",
                width: 100,
                sortable: true
            },
            {
                label: "Delivery date",
                dataKey: "delivery_datetime",
                formatData: "datetime_local", //datetime
                flexGrow: 1,
                minWidth: 180,
                // title: true,
                sortable: true
            },
            {
                label: "Delivery status",
                dataKey: "delivery_status",
                flexGrow: 1,
                minWidth: 120,
                // title: true,
                sortable: true
            },
            {
                label: "Margin",
                fullLabel: "Margin",
                dataKey: "margin",
                value: ({ margin, margin_cur_name }) => {
                    return renderMoney(margin, margin_cur_name, 5)
                },
                width: 110,
                sortable: true,
                align: "right"
            },
            {
                label: "Part order",
                dataKey: "mpart_order",
                minWidth: 220,
                sortable: true
            },
            {
                label: "Part id",
                dataKey: "mpart_id",
                flexGrow: 1,
                minWidth: 295,
                sortable: true
            },
            {
                label: "Message",
                dataKey: "message",
                value: ({ message }) => {
                    return (
                        <Whisper
                            enterable
                            placement="bottomStart"
                            speaker={
                                <Tooltip>{message}</Tooltip>
                            }
                        >
                            <span>{message}</span>
                        </Whisper>
                    )
                },
                minWidth: 340,
                sortable: true
            },
            {
                label: "Message ID",
                dataKey: "message_id",
                flexGrow: 1,
                minWidth: 295,
                // title: true,
                sortable: true
            }
        ];

        const mdrFailedColumns = [
            {
                label: "Date Time",
                dataKey: "datetime",
                formatData: "datetime_local", //datetime_en
                flexGrow: 1,
                minWidth: 180,
                title: true,
                sortable: true
            },
            {
                label: "Operator",
                dataKey: "operator",
                minWidth: 200,
                sortable: true
            },
            {
                label: "Country",
                dataKey: "country",
                minWidth: 160,
                sortable: true
            },
            {
                label: "Supplier",
                dataKey: "supplier_name",
                minWidth: 200,
                sortable: true
            },
            {
                label: "Supplier Rate",
                dataKey: "supplier_rate",
                fullLabel: "Supplier Rate",
                value: ({ supplier_rate, supplier_cur_name }) => {
                    return renderMoney(supplier_rate, supplier_cur_name);
                },
                minWidth: 120,
                sortable: true,
                align: "right"
            },
            {
                label: "Sender ID",
                dataKey: "senderid",
                width: 160,
                sortable: true
            },
            {
                label: "Supplier trunk",
                dataKey: "supplier_trunk",
                minWidth: 160,
                // sortable: true
            },
            {
                label: "Phone",
                dataKey: "phone",
                minWidth: 180,
                sortable: true
            },
            {
                label: "Origin",
                dataKey: "sender_sde_name",
                minWidth: 200,
                title: true,
                sortable: true
            },
            {
                label: "Destination",
                dataKey: "phone_sde_name",
                minWidth: 280,
                sortable: true
            },
            {
                label: "MCC/MNC",
                dataKey: "mcc_mnc",
                minWidth: 100,
                sortable: true
            },
            {
                label: "Reason",
                dataKey: "reason",
                value: ({ reason }) => {
                    return <a title={reason}>
                        {reason}
                    </a>
                },
                minWidth: 300,
                sortable: true
            },
            {
                label: "Part order",
                dataKey: "mpart_order",
                minWidth: 220,
                sortable: true
            },
            {
                label: "Part id",
                dataKey: "mpart_id",
                flexGrow: 1,
                minWidth: 295,
                sortable: true
            },
            {
                label: "Message",
                dataKey: "message",
                value: ({ message }) => {
                    return (
                        <Whisper
                            enterable
                            placement="bottomStart"
                            speaker={
                                <Tooltip>{message}</Tooltip>
                            }
                        >
                            <span>{message}</span>
                        </Whisper>
                    )
                },
                minWidth: 340,
                sortable: true
            }
        ];

        return isFailedMdr ? mdrFailedColumns : servicePick(service, cdrColumns, mdrColumns);
    }, [service, isFailedMdr]);

    const filteredColumns = useMemo(() => {
        return columns.filter(column => Array.isArray(columnSelector) ? columnSelector.includes(column.dataKey) : true);
    }, [columns, columnSelector]);

    const normalizedFilters = filters;

    const getCDRItems = (page = 1, perPage = currentPerPage, _sort = sort) => {
        if (isFailedMdr) {
            props.getFailedMDRList(normalizedFilters, page, perPage, sort);
            return;
        }
        props.getCDRList(service, normalizedFilters, page, perPage, sort);
    };

    useEffect(() => {
        if (isFailedMdr === prevIsFailedMdr && service === prevService) {
            getCDRItems();
        }
    }, [service, normalizedFilters, isFailedMdr, currentPerPage]);

    return (
        <Table
            data={dataList}
            service={service}
            loading={loading}
            columns={filteredColumns}
            originalColumns={columns}
            resizable
            count={count}
            per_page={currentPerPage}
            page={page}
            getItems={getCDRItems}
            // showedCount={more ? count : <><StyledBoldText>&gt;</StyledBoldText>{count}</>}
            showedCount={count}
            ispagination
            additionalColumnUpdate={isFailedMdr}
            columnSelectorLSKey={columnSelectorLSKey}
            defaultSortColumn="datetime"
            defaultSortType={DESC_SORT}
            perPageLSKey={perPageLSKey}
            onSort={(column, type) => setSort({ column, type })}
            onChangePerPage={setCurrentPerPage}
            customChangePerPage
        />
    );
};

const mapStateToProps = ({ cdr, auth }, ownProps) => ({
    cdrMdrLoading: cdr.loading,
    cdrMdrList: cdr.list,
    cdrMdrMore: cdr.more,
    cdrMdrCount: cdr.count,

    mdrFailedLoading: cdr.mdrFailedLoading,
    mdrFailedList: cdr.mdrFailedList,
    mdrFailedMore: cdr.mdrFailedMore,
    mdrFailedCount: cdr.mdrFailedCount,
    mdrFailedPerPage: cdr.mdrFailedPerPage,
    cdrPerPage: cdr.perPage,
    perPage: ownProps.isFailedMdr ? (cdr.mdrFailedPerPage ?? cdr.perPage) : cdr.perPage,
    page: ownProps.isFailedMdr ? (cdr.mdrFailedPage ?? cdr.page) : cdr.page,
    service: auth.service
});

const mapDispatchToProps = {
    getCDRList,
    getFailedMDRList
};

export default connect(mapStateToProps, mapDispatchToProps)(CDRTable);
